/**
 * SettlementTable Component
 * 라이더 선정산내역 테이블 영역
 * - 컬럼 고정 (NO, 라이더ID, 라이더명)
 * - 정렬 기능 (부모 컴포넌트에서 관리)
 * - 검색 결과 건수 표시
 * - 라이더 상세 모달
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { RiderPreSettlement, SortConfig } from '../types/riderPreSettlement.types';
import {
    TABLE_COLUMNS,
    shouldShowColumn,
    formatCurrency,
    getColumnAlignment,
    getPreSettlementValueByIndex,
} from '../utils/riderPreSettlementHelpers';
import HeaderTooltip from '../../../shared/components/HeaderTooltip';
import RiderPreDetailModal from './RiderPreDetailModal';
import '../styles/RiderPreSettlementHistory.css';

interface SettlementTableProps {
    settlements: RiderPreSettlement[];
    currentItems: RiderPreSettlement[];
    currentPage: number;
    loading: boolean;
    isRiderUser: boolean;
    sortConfig: SortConfig;
    onSort: (columnIndex: number) => void;
}

const SettlementTable: React.FC<SettlementTableProps> = ({
                                                             settlements,
                                                             currentItems,
                                                             currentPage,
                                                             loading,
                                                             isRiderUser,
                                                             sortConfig,
                                                             onSort
                                                         }) => {
    const [selectedSettlement, setSelectedSettlement] = useState<RiderPreSettlement | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [activeTooltip, setActiveTooltip] = useState<number | null>(null);
    const headerRefs = useRef<{ [key: number]: HTMLTableCellElement | null }>({});

    const fixedBodyRef = useRef<HTMLTableSectionElement>(null);
    const scrollableBodyRef = useRef<HTMLTableSectionElement>(null);
    const scrollableContainerRef = useRef<HTMLDivElement>(null);

    const FIXED_COLUMN_INDICES = [1, 3, 4];

    const visibleColumns = TABLE_COLUMNS.filter(col => shouldShowColumn(col.index, isRiderUser));
    const fixedColumns = visibleColumns.filter(col => FIXED_COLUMN_INDICES.includes(col.index));
    const scrollableColumns = visibleColumns.filter(col => !FIXED_COLUMN_INDICES.includes(col.index));

    const handleRowClick = useCallback((settlement: RiderPreSettlement) => {
        setSelectedSettlement(settlement);
        setShowDetailModal(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setShowDetailModal(false);
        setSelectedSettlement(null);
    }, []);

    useEffect(() => {
        const scrollableContainer = scrollableContainerRef.current;
        const fixedBody = fixedBodyRef.current;

        if (!scrollableContainer || !fixedBody) return;

        const handleScroll = () => {
            if (fixedBody.parentElement) {
                fixedBody.parentElement.scrollTop = scrollableContainer.scrollTop;
            }
        };

        scrollableContainer.addEventListener('scroll', handleScroll);
        return () => scrollableContainer.removeEventListener('scroll', handleScroll);
    }, []);

    const renderSortIcon = (columnIndex: number) => {
        const isSorted = sortConfig.columnIndex === columnIndex;
        const direction = isSorted ? sortConfig.direction : null;

        return (
            <span className="sort-icon">
                {direction === 'asc' && <i className="fas fa-sort-up"></i>}
                {direction === 'desc' && <i className="fas fa-sort-down"></i>}
                {!direction && <i className="fas fa-sort" style={{ opacity: 0.3 }}></i>}
            </span>
        );
    };

    /**
     * 셀 값 렌더링
     * - 컬럼 1(NO): 현재 페이지 기준 순번
     * - 컬럼 8~24: getPreSettlementValueByIndex로 필드 값 취득 후 formatCurrency 적용
     * - 컬럼 2~7: getPreSettlementValueByIndex로 문자열 필드 값 그대로 반환
     */
    const renderCellValue = (
        settlement: RiderPreSettlement,
        columnIndex: number,
        rowIndex: number
    ): React.ReactNode => {
        if (columnIndex === 1) return (currentPage - 1) * 10 + rowIndex + 1;

        const value = getPreSettlementValueByIndex(settlement, columnIndex);
        if (columnIndex >= 8) return formatCurrency(value as number);
        return value;
    };

    const getCellProps = (columnIndex: number): { className?: string; style?: React.CSSProperties } => {
        const alignment = getColumnAlignment(columnIndex);
        const isBold = columnIndex === 25 || columnIndex === 26;

        if (alignment.textAlign === 'center') {
            return { className: 'text-center' };
        }
        if (alignment.textAlign === 'right') {
            return { style: { textAlign: 'right', fontWeight: isBold ? 'bold' : undefined } };
        }
        return {};
    };

    const renderHeader = (column: typeof TABLE_COLUMNS[number]) => {
        const hasTooltip = !!column.tooltip;
        return (
            <th
                key={column.index}
                ref={el => { headerRefs.current[column.index] = el; }}
                style={{ width: column.width, minWidth: column.minWidth }}
                className={`sortable ${sortConfig.columnIndex === column.index ? 'sorted' : ''} ${hasTooltip ? 'has-tooltip' : ''}`}
                onClick={() => onSort(column.index)}
                onMouseEnter={() => hasTooltip && setActiveTooltip(column.index)}
                onMouseLeave={() => setActiveTooltip(null)}
            >
                <span className="header-content">
                    {column.header}
                    {hasTooltip && <i className="fas fa-info-circle tooltip-icon"></i>}
                </span>
                {renderSortIcon(column.index)}
                {hasTooltip && column.tooltip && (
                    <HeaderTooltip
                        content={column.tooltip}
                        targetRef={{ current: headerRefs.current[column.index] }}
                        visible={activeTooltip === column.index}
                        tooltipClassName="rider-pre-settlement-header-tooltip"
                    />
                )}
            </th>
        );
    };

    return (
        <>
            <div className="result-count">
                <i className="fas fa-list"></i>
                검색 결과: <strong>{settlements.length.toLocaleString()}건</strong>
            </div>

            <div className="rider-pre-settlement-table-container">
                <div className="rider-pre-settlement-table-wrapper">
                    {/* 고정 컬럼 영역 */}
                    <div className="fixed-columns">
                        <table className="rider-pre-settlement-table">
                            <thead>
                            <tr>{fixedColumns.map(renderHeader)}</tr>
                            </thead>
                            <tbody ref={fixedBodyRef}>
                            {!loading && settlements.length === 0 ? (
                                <tr>
                                    <td colSpan={fixedColumns.length} className="text-center text-gray-600"></td>
                                </tr>
                            ) : (
                                currentItems.map((settlement, index) => (
                                    <tr key={settlement.no} onClick={() => handleRowClick(settlement)}>
                                        {fixedColumns.map(column => (
                                            <td key={column.index} {...getCellProps(column.index)}>
                                                {renderCellValue(settlement, column.index, index)}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* 스크롤 가능 컬럼 영역 */}
                    <div className="scrollable-columns" ref={scrollableContainerRef}>
                        <table className="rider-pre-settlement-table">
                            <thead>
                            <tr>{scrollableColumns.map(renderHeader)}</tr>
                            </thead>
                            <tbody ref={scrollableBodyRef}>
                            {!loading && settlements.length === 0 ? (
                                <tr>
                                    <td colSpan={scrollableColumns.length} className="text-center text-gray-600">
                                        조회된 정산내역이 없습니다.
                                    </td>
                                </tr>
                            ) : (
                                currentItems.map((settlement, index) => (
                                    <tr key={settlement.no} onClick={() => handleRowClick(settlement)}>
                                        {scrollableColumns.map(column => (
                                            <td key={column.index} {...getCellProps(column.index)}>
                                                {renderCellValue(settlement, column.index, index)}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {showDetailModal && selectedSettlement && (
                <RiderPreDetailModal
                    settlement={selectedSettlement}
                    onClose={handleCloseModal}
                    isRiderUser={isRiderUser}
                />
            )}
        </>
    );
};

export default SettlementTable;
